import {FC, useMemo} from 'react'
import type {QHSEDefectDto, QHSENearMissReportDto} from '../../core/_models'
import type {SourceErrors} from './types'
import {ArrowRight} from 'lucide-react'
import {chartNearMisses} from './dashboardData'

const BarChart: FC<{title: string; description: string; values: {label: string; count: number}[]; error?: string; onViewAll?: () => void}> =
  ({title, description, values, error, onViewAll}) => {
    const max = Math.max(1, ...values.map(item => item.count))
    return <section aria-label={title} className='bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
      <div className='flex items-center justify-between gap-3'>
        <h6 className='text-sm font-bold text-slate-900'>{title}</h6>
        <button onClick={onViewAll} disabled={!!error} className='text-[10px] font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors'>
          View all <ArrowRight className='w-3 h-3' />
        </button>
      </div>
      <p className='text-[10px] text-slate-500 mt-1 mb-4'>{description}</p>
      {error ? <p role='status' className='text-sm text-slate-500'>{error}</p> :
        values.length === 0 ? <p className='text-sm text-slate-500'>No records in the selected scope.</p> : <ul className='space-y-2' aria-label={`${title} values`}>
          {values.map(item => <li key={item.label} className='flex items-center gap-2 text-[11px]'>
            <span className='w-24 shrink-0 text-slate-600'>{item.label}</span>
            <span className='flex-1 h-3 bg-slate-100 rounded overflow-hidden' aria-hidden='true'>
              <span className='block h-full bg-blue-500 rounded' style={{width: `${item.count / max * 100}%`}} />
            </span>
            <span className='w-8 text-right font-semibold'>{item.count}</span>
          </li>)}
        </ul>}
    </section>
  }

const DashboardCharts: FC<{defects: QHSEDefectDto[]; nearMisses: QHSENearMissReportDto[]; errors: SourceErrors; onViewDefects?: () => void; onViewNearMisses?: () => void}> =
  ({defects, nearMisses, errors, onViewDefects, onViewNearMisses}) => {
    const statuses = useMemo(() => {
      const counts = new Map<string, number>()
      defects.forEach(defect => {
        const status = defect.status || 'Unknown'
        counts.set(status, (counts.get(status) || 0) + 1)
      })
      return Array.from(counts, ([label, count]) => ({label: label.replace(/_/g, ' '), count}))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    }, [defects])
    const monthly = useMemo(() => {
      const now = new Date()
      const months = Array.from({length: 6}, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1)
        return {key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
          label: date.toLocaleDateString(undefined, {month: 'short', year: '2-digit'}), count: 0}
      })
      chartNearMisses(nearMisses, now).forEach(report => {
        const date = report.dateOfOccurrence || report.dateIssued || report.createdAt
        const month = months.find(item => date?.startsWith(item.key))
        if (month) month.count++
      })
      return months
    }, [nearMisses])
    return <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>
      <BarChart title='Defects by status' description='Current active defect reports by their saved status.'
        values={statuses} error={errors.defects} onViewAll={onViewDefects} />
      <BarChart title='Near miss reports by month' description='Occurrences over the last 6 calendar months; issue/creation date used when occurrence date is missing.'
        values={monthly} error={errors.nearMisses} onViewAll={onViewNearMisses} />
    </div>
  }

export default DashboardCharts
